import { toastController } from '@ionic/vue';
import { useLocale } from '@/common/composables/useLocale';
import { logger } from '@/common/services/config';

export interface NotificationOptions {
  duration?: number;
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'light'
    | 'medium'
    | 'dark';
  position?: 'top' | 'middle' | 'bottom';
}

class NotificationService {
  private getTranslation: ((key: string, values?: Record<string, any>) => string) | null = null;

  initialize() {
    const { t } = useLocale();
    this.getTranslation = t;
  }

  private async showToast(message: string, options: NotificationOptions = {}) {
    const { duration = 3000, color = 'primary', position = 'bottom' } = options;

    const toast = await toastController.create({
      message,
      duration,
      color,
      position,
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
        },
      ],
    });

    await toast.present();
  }

  async success(messageKey: string, values?: Record<string, any>, options?: NotificationOptions) {
    if (!this.getTranslation) {
      logger.warn('NotificationService not initialized');
      return;
    }

    const message = this.getTranslation(messageKey, values);
    await this.showToast(message, { ...options, color: 'success' });
  }

  async error(messageKey: string, values?: Record<string, any>, options?: NotificationOptions) {
    if (!this.getTranslation) {
      logger.warn('NotificationService not initialized');
      return;
    }

    const message = this.getTranslation(messageKey, values);
    await this.showToast(message, { ...options, color: 'danger' });
  }

  async info(messageKey: string, values?: Record<string, any>, options?: NotificationOptions) {
    if (!this.getTranslation) {
      logger.warn('NotificationService not initialized');
      return;
    }

    const message = this.getTranslation(messageKey, values);
    await this.showToast(message, { ...options, color: 'primary' });
  }

  async warning(messageKey: string, values?: Record<string, any>, options?: NotificationOptions) {
    if (!this.getTranslation) {
      logger.warn('NotificationService not initialized');
      return;
    }

    const message = this.getTranslation(messageKey, values);
    await this.showToast(message, { ...options, color: 'warning' });
  }
}

export const notificationService = new NotificationService();
